import {
  NotificationsActiveOutlined,
  NotificationsNoneOutlined,
  NotificationsOffOutlined,
} from "@mui/icons-material"
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material"
import { useCallback, useEffect, useMemo, useState } from "react"
import toast from "react-hot-toast"
import useAuth from "../../hooks/useAuth"
import {
  deleteCurrentFcmToken,
  getCurrentFcmToken,
  isPushMessagingSupported,
  onForegroundPushMessage,
} from "../../service/firebase-messaging"
import {
  getPushSubscription,
  // logPushSubscriptionError,
  savePushSubscription,
  unsubscribePushSubscription,
} from "../../service/pwa-push-notification"
import {
  getClientInfo,
  getPushDeviceId,
} from "../../service/push-notification-device"

const PERMISSION_DENIED = "denied"
const PERMISSION_GRANTED = "granted"

const getNotificationPermission = () => {
  if (!("Notification" in window)) return "unsupported"
  return Notification.permission
}

const PwaPushNotificationSubscription = () => {
  const auth = useAuth()
  const token = auth?.token
  const [open, setOpen] = useState(false)
  const [supported, setSupported] = useState(false)
  const [initializing, setInitializing] = useState(true)
  const [saving, setSaving] = useState(false)
  const [permission, setPermission] = useState(getNotificationPermission)
  const [subscription, setSubscription] = useState(null)

  const deviceId = useMemo(() => getPushDeviceId(), [])
  const isSubscribed = Boolean(subscription?.isActive)

  const logError = useCallback(
    (error, context) =>
      // logPushSubscriptionError({ token, error, context }).catch(() => {}),
    // [token]
      console.error(`Push subscription error (${context}):`, error),
    []
  )

  const syncSubscription = useCallback(async () => {
    if (!token) return

    const currentPermission = getNotificationPermission()
    setPermission(currentPermission)

    const existingSubscription = await getPushSubscription({ token, deviceId })
    setSubscription(existingSubscription)

    if (currentPermission === PERMISSION_DENIED && existingSubscription?.id) {
      const inactiveSubscription = await unsubscribePushSubscription({
        token,
        deviceId,
        permissionStatus: currentPermission,
      })
      setSubscription(inactiveSubscription)
      return
    }

    if (currentPermission !== PERMISSION_GRANTED) return

    if (existingSubscription?.id && !existingSubscription?.isActive) return

    const fcmToken = await getCurrentFcmToken()
    if (!fcmToken) return

    const updatedSubscription = await savePushSubscription({
      token,
      fcmToken,
      deviceId,
      permissionStatus: currentPermission,
      clientInfo: getClientInfo(),
    })
    setSubscription(updatedSubscription)
  }, [deviceId, token])

  useEffect(() => {
    let isMounted = true
    isPushMessagingSupported()
      .then((isSupported) => {
        if (!isMounted) return
        setSupported(isSupported)
        if (!isSupported || !token) return null
        return syncSubscription()
      })
      .catch((error) => {
        logError(error, "initialization")
      })
      .finally(() => {
        if (isMounted) setInitializing(false)
      })

    return () => {
      isMounted = false
    }
  }, [logError, syncSubscription, token])

  useEffect(() => {
    if (!token || !supported) return undefined

    let unsubscribe = () => {}
    onForegroundPushMessage((payload) => {
      const title = payload?.notification?.title || "New notification"
      const body = payload?.notification?.body
      toast(body ? `${title}: ${body}` : title)
    })
      .then((unsubscribeHandler) => {
        unsubscribe = unsubscribeHandler
      })
      .catch((error) => logError(error, "foreground-message"))

    return () => unsubscribe()
  }, [logError, supported, token])

  const handleSubscribe = async () => {
    try {
      setSaving(true)
      const nextPermission = await Notification.requestPermission()
      setPermission(nextPermission)

      if (nextPermission !== PERMISSION_GRANTED) {
        toast.error("Notification permission was not enabled")
        return
      }

      const fcmToken = await getCurrentFcmToken()
      if (!fcmToken) {
        toast.error("Unable to create notification token")
        return
      }

      const updatedSubscription = await savePushSubscription({
        token,
        fcmToken,
        deviceId,
        permissionStatus: nextPermission,
        clientInfo: getClientInfo(),
      })
      setSubscription(updatedSubscription)
      toast.success("Notifications enabled")
    } catch (error) {
      logError(error, "subscribe")
      toast.error("Unable to enable notifications")
    } finally {
      setSaving(false)
    }
  }

  const handleUnsubscribe = async () => {
    try {
      setSaving(true)
      const inactiveSubscription = await unsubscribePushSubscription({
        token,
        deviceId,
        permissionStatus: getNotificationPermission(),
      })
      setSubscription(inactiveSubscription)
      deleteCurrentFcmToken().catch((error) => logError(error, "delete-token"))
      toast.success("Notifications disabled")
    } catch (error) {
      logError(error, "unsubscribe")
      toast.error("Unable to disable notifications")
    } finally {
      setSaving(false)
    }
  }

  if (!token || initializing || !supported) return null

  const icon = isSubscribed ? (
    <NotificationsActiveOutlined color="primary" />
  ) : permission === PERMISSION_DENIED ? (
    <NotificationsOffOutlined />
  ) : (
    <NotificationsNoneOutlined />
  )

  return (
    <>
      <IconButton
        aria-label="Notification settings"
        onClick={() => setOpen(true)}
        size="large"
      >
        {icon}
      </IconButton>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Notification Settings</DialogTitle>
        <DialogContent>
          {permission === PERMISSION_DENIED ? (
            <DialogContentText>
              Notifications are blocked for this browser. Enable them from site
              settings to receive updates.
            </DialogContentText>
          ) : (
            <Box sx={{ minWidth: 260 }}>
              <Typography sx={{ mb: 1 }}>
                {isSubscribed
                  ? "Notifications are enabled for this device."
                  : "Enable notifications for offers and loyalty updates."}
              </Typography>
              <DialogContentText>
                This setting applies only to the current browser/device.
              </DialogContentText>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
          {permission !== PERMISSION_DENIED &&
            (isSubscribed ? (
              <Button
                color="error"
                disabled={saving}
                onClick={handleUnsubscribe}
              >
                Disable
              </Button>
            ) : (
              <Button disabled={saving} onClick={handleSubscribe}>
                Enable
              </Button>
            ))}
        </DialogActions>
      </Dialog>
    </>
  )
}

export default PwaPushNotificationSubscription
